// src/pages/Contacts.js 
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Toolbar,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Stack,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TablePagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  GetApp as GetAppIcon,
  MarkEmailRead as MarkEmailReadIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  MailOutline as MailOutlineIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';

import { db } from '../firebase'; // même import que Dashboard
import {
  collection,
  getDocs,
  query,
  orderBy,
  updateDoc,
  doc,
} from 'firebase/firestore';

import { Layout } from './Layout'; // comme Dashboard

function formatTS(ts) {
  if (!ts) return '—';
  const d = ts?.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString('fr-BE');
}

function statusColor(status) {
  switch (status) {
    case 'new': return 'primary';
    case 'in_progress': return 'warning';
    case 'closed': return 'success';
    default: return 'default';
  }
}

function statusLabel(status) {
  switch (status) {
    case 'new': return 'Nouveau';
    case 'in_progress': return 'En cours';
    case 'closed': return 'Fermé';
    default: return '—';
  }
}

export default function Contacts() {
  const [loading, setLoading] = useState(true);
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all'); // general/course/registration/other/formation
  const [statusFilter, setStatusFilter] = useState('all'); // new/in_progress/closed
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selected, setSelected] = useState(null); // contact sélectionné (fiche détail)
  const [saving, setSaving] = useState(false);

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, 'contacts'), orderBy('createdAt', 'desc'));
      const snap = await getDocs(q);
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setContacts(list);
    } catch (e) {
      console.error('Erreur chargement contacts: ', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  const normalized = useMemo(() => {
    return contacts.map(c => {
      const type = c.contactType || c.type || '—'; // About = contactType ; Crew = type:"formation"
      const fullName = `${c.firstName || ''} ${c.lastName || ''}`.trim() || '—';
      const status = c.status || 'new';
      return { ...c, type, fullName, status };
    });
  }, [contacts]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return normalized.filter(c => {
      const matchesSearch =
        !s ||
        c.fullName.toLowerCase().includes(s) ||
        (c.email || '').toLowerCase().includes(s) ||
        (c.phone || '').toLowerCase().includes(s) ||
        (c.message || '').toLowerCase().includes(s);

      const matchesType = typeFilter === 'all' || c.type === typeFilter;
      const matchesStatus = statusFilter === 'all' || (c.status || 'new') === statusFilter;
      return matchesSearch && matchesType && matchesStatus;
    });
  }, [normalized, search, typeFilter, statusFilter]);

  const paged = useMemo(() => {
    const start = page * rowsPerPage;
    return filtered.slice(start, start + rowsPerPage);
  }, [filtered, page, rowsPerPage]);

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = e => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const updateStatus = async (id, newStatus) => {
    try {
      setSaving(true);
      await updateDoc(doc(db, 'contacts', id), { status: newStatus });
      setContacts(prev => prev.map(c => (c.id === id ? { ...c, status: newStatus } : c)));
    } catch (e) {
      console.error('Erreur maj statut: ', e);
    } finally {
      setSaving(false);
    }
  };

  const exportCSV = () => {
    const headers = [
      'Date',
      'Statut',
      'Type',
      'Prénom',
      'Nom',
      'Email',
      'Téléphone',
      'CourseId',
      'Message',
    ];
    const rows = filtered.map(c => [
      formatTS(c.createdAt),
      statusLabel(c.status || 'new'),
      c.type || '—',
      c.firstName || '',
      c.lastName || '',
      c.email || '',
      c.phone || '',
      c.courseId || '',
      (c.message || '').replace(/\n/g, ' '),
    ]);

    const csvContent =
      [headers, ...rows]
        .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
        .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    a.download = `contacts-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Layout>
      <Box sx={{ p: 3, bgcolor: '#F9FAFB', minHeight: '100vh' }}>
        <Typography variant="h4" sx={{ fontWeight: 800, mb: 2 }}>
          Contacts
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
          Messages reçus via le site (About & Formation Crew). Recherche, filtres, statut, fiche détail et export CSV.
        </Typography>

        <Paper sx={{ mb: 2, borderRadius: 2, overflow: 'hidden' }}>
          <Toolbar sx={{ gap: 2, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Rechercher (nom, email, téléphone, message)"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
              sx={{ flex: 1, minWidth: 260 }}
            />

            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Type</InputLabel>
              <Select
                label="Type"
                value={typeFilter}
                onChange={(e) => { setTypeFilter(e.target.value); setPage(0); }}
              >
                <MenuItem value="all">Tous</MenuItem>
                <MenuItem value="general">Général</MenuItem>
                <MenuItem value="course">Cours</MenuItem>
                <MenuItem value="registration">Inscription</MenuItem>
                <MenuItem value="other">Autre</MenuItem>
                <MenuItem value="formation">Formation</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Statut</InputLabel>
              <Select
                label="Statut"
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
              >
                <MenuItem value="all">Tous</MenuItem>
                <MenuItem value="new">Nouveau</MenuItem>
                <MenuItem value="in_progress">En cours</MenuItem>
                <MenuItem value="closed">Fermé</MenuItem>
              </Select>
            </FormControl>

            <Stack direction="row" spacing={1} sx={{ ml: 'auto' }}>
              <Tooltip title="Exporter CSV (filtres appliqués)">
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<GetAppIcon />}
                    onClick={exportCSV}
                    disabled={loading || filtered.length === 0}
                  >
                    Exporter
                  </Button>
                </span>
              </Tooltip>

              <Tooltip title="Rafraîchir">
                <IconButton onClick={fetchContacts}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Toolbar>

          <Divider />

          {loading ? (
            <Box sx={{ p: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress sx={{ color: '#7C3AED' }} />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Nom</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Téléphone</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Message</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Statut</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paged.map((c) => (
                      <TableRow key={c.id} hover>
                        <TableCell sx={{ maxWidth: 180 }}>
                          <Typography variant="body2" fontWeight={600}>
                            {c.fullName || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 220 }}>
                          {c.email ? (
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <MailOutlineIcon fontSize="small" />
                              <a href={`mailto:${c.email}`} style={{ textDecoration: 'none' }}>
                                {c.email}
                              </a>
                            </Stack>
                          ) : '—'}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 160 }}>
                          {c.phone ? (
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <PhoneIcon fontSize="small" />
                              <a href={`tel:${c.phone}`} style={{ textDecoration: 'none' }}>
                                {c.phone}
                              </a>
                            </Stack>
                          ) : '—'}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 140 }}>
                          <Chip
                            size="small"
                            label={c.type}
                            sx={{ textTransform: 'capitalize' }}
                          />
                        </TableCell>
                        <TableCell sx={{ maxWidth: 280 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden'
                            }}
                          >
                            {c.message || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {formatTS(c.createdAt)}
                        </TableCell>
                        <TableCell>
                          <Chip size="small" color={statusColor(c.status)} label={statusLabel(c.status)} />
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Tooltip title="Voir le détail">
                              <IconButton onClick={() => setSelected(c)}>
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Marquer en cours">
                              <span>
                                <IconButton
                                  disabled={saving || c.status === 'in_progress'}
                                  onClick={() => updateStatus(c.id, 'in_progress')}
                                >
                                  <MarkEmailReadIcon />
                                </IconButton>
                              </span>
                            </Tooltip>

                            <Tooltip title="Fermer">
                              <span>
                                <IconButton
                                  disabled={saving || c.status === 'closed'}
                                  onClick={() => updateStatus(c.id, 'closed')}
                                >
                                  <CheckCircleIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                    {paged.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                          Aucun contact pour ces filtres.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={filtered.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50]}
              />
            </>
          )}
        </Paper>
      </Box>

      {/* Détail */}
      <Dialog open={!!selected} onClose={() => setSelected(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Détail du contact
          <IconButton onClick={() => setSelected(null)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selected && (
            <Stack spacing={1.2}>
              <Stack direction="row" spacing={1.5}>
                <Chip size="small" color={statusColor(selected.status)} label={statusLabel(selected.status)} />
                <Chip size="small" label={`Type: ${selected.type}`} sx={{ textTransform: 'capitalize' }} />
              </Stack>

              <Divider />

              <Typography variant="body2"><strong>Nom:</strong> {selected.fullName || '—'}</Typography>
              <Typography variant="body2"><strong>Email:</strong> {selected.email || '—'}</Typography>
              <Typography variant="body2"><strong>Téléphone:</strong> {selected.phone || '—'}</Typography>
              <Typography variant="body2"><strong>CourseId:</strong> {selected.courseId || '—'}</Typography>
              <Typography variant="body2"><strong>Date:</strong> {formatTS(selected.createdAt)}</Typography>

              <Divider />

              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                <strong>Message:</strong><br />{selected.message || '—'}
              </Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {selected && (
            <>
              <Button
                startIcon={<MarkEmailReadIcon />}
                onClick={() => updateStatus(selected.id, 'in_progress')}
                disabled={saving || selected.status === 'in_progress'}
              >
                Marquer “En cours”
              </Button>
              <Button
                startIcon={<CheckCircleIcon />}
                onClick={() => updateStatus(selected.id, 'closed')}
                disabled={saving || selected.status === 'closed'}
              >
                Fermer
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Layout>
  );
}
